/**
 * Leaderboard endpoints (see api/src/modules/leaderboard/leaderboard.controller.ts).
 *
 * Public:
 *   GET /leaderboard?week=N&limit=&offset= — top-N entries
 *
 * Authenticated (either ACCESS or GAME token via authenticateAny):
 *   GET /leaderboard/me?week=N — the caller's rank for that week, or
 *   { hasScore: false, rank: null } when no score recorded yet.
 */
import { apiJson } from '../api';

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string;
  highScore: number;
  /** ISO timestamp the score was achieved; never null in practice for any
   * row that lands on the leaderboard, but the API types it as nullable. */
  achievedAt: string | null;
}

export interface LeaderboardResponse {
  status: number;
  data: {
    week: number;
    total: number;
    limit: number;
    offset: number;
    entries: LeaderboardEntry[];
  };
}

export interface MyRankResponse {
  status: number;
  data:
    | { week: number; hasScore: false; rank: null; highScore: 0; achievedAt: null }
    | { week: number; hasScore: true; rank: number; highScore: number; achievedAt: string };
}

export async function getLeaderboard(
  week: number,
  limit = 10,
  offset = 0,
  init?: RequestInit,
): Promise<LeaderboardResponse['data']> {
  const qs = new URLSearchParams({ week: String(week), limit: String(limit), offset: String(offset) });
  const res = await apiJson<LeaderboardResponse>(`/leaderboard?${qs.toString()}`, init);
  return res.data;
}

export async function getMyRank(
  week: number,
  init?: RequestInit,
): Promise<MyRankResponse['data']> {
  const qs = new URLSearchParams({ week: String(week) });
  const res = await apiJson<MyRankResponse>(`/leaderboard/me?${qs.toString()}`, init);
  return res.data;
}
